import { useEffect, useMemo, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/hook";
import KPICard from "./KPICard";
import LineChart from "./LineChart";
import BarChart from "./BarChart";
import DataTable from "./DataTable";
import { Sighting } from "../../../models/SherlockModel";
import ComponentLoadingBar from "../../utility/ComponentLoadingBar";

const SightingsLayout = () => {
    const totalCounts = useAppSelector((s: any) => s.sherlock.totalCounts);
    const dailyMetrics = useAppSelector((s: any) => s.sherlock.dailyMetrics);
    const weeklyMetrics = useAppSelector((s: any) => s.sherlock.weeklyMetrics);
    const streamItems = useAppSelector((s: any) => s.sherlock.streamSightings);
    const tableItems = useAppSelector((s: any) => s.sherlock.pagedSightings);

    const [localStreamItems, setLocalStreamItems] = useState<Sighting[]>([]);

    const [streamEnabled, setStreamEnabled] = useState(true);
    const toggleStream = () => setStreamEnabled((v) => !v);

    const [period, setPeriod] = useState(7);
    const [historyDataInPeriod, setHistoryDataInPeriod] = useState<{ label: string; points: { x: string; y: number }[] }>({ label: "Count", points: [] });

    const kpiSightings = (totalCounts?.sightings_count ?? 0) + new Set(localStreamItems.map((item: Sighting) => item.id)).size;
    const kpiIndicators = totalCounts?.indicators_count ?? 0;

    const dispatch = useAppDispatch();

    const historyData = useMemo(() => {
        if (!weeklyMetrics?.length) return { label: "Count", points: [] as { x: string; y: number }[] };
        const pts: { x: string; y: number }[] = [];
        weeklyMetrics.forEach((m: any) => {
            const sum = m.daily_counts.reduce((acc: number, r: Record<string, number>) => acc + (Object.values(r)[0] ?? 0), 0);
            pts.unshift({ x: m.id, y: sum });
        });
        return { label: "Count", points: pts };
    }, [weeklyMetrics]);

    useEffect(() => {
        const historyDataInPeriod = historyData.points.length > period ?
            { label: historyData.label, points: historyData.points.slice(-period) }
            : { label: historyData.label, points: historyData.points };
        setHistoryDataInPeriod(historyDataInPeriod);
    }, [historyData, period]);

    useEffect(() => {
        if (streamEnabled) {
            setLocalStreamItems((items) => {
                const newItems = [...streamItems, ...items];
                if (newItems.length > 500) {
                    return newItems.slice(0, 500);
                } else {
                    return newItems;
                }
            });
        }
    }, [streamItems, streamEnabled]);

    const todayBars = useMemo(() => {
        const baseBars = (dailyMetrics ?? []).map((d: Record<string, number>) => ({
            x: Object.keys(d)[0],
            y: Object.values(d)[0]
        }));

        // Count unique stream sightings by client_name
        const streamCounts: Record<string, number> = {};
        const seenIds = new Set<string>();

        localStreamItems.forEach((item: Sighting) => {
            if (!seenIds.has(item.id)) {
                seenIds.add(item.id);
                streamCounts[item.client_name] = (streamCounts[item.client_name] || 0) + 1;
            }
        });

        // Merge stream counts with base bars
        return baseBars.map((bar: any) => ({
            x: bar.x,
            y: bar.y + (streamCounts[bar.x] || 0)
        }));
    }, [dailyMetrics, localStreamItems]);

    const weeklyLoading = !weeklyMetrics?.length
    const dailyLoading = (dailyMetrics ?? []).length === 0
    const tableLoading = tableItems.length === 0

    return (
        <div className="w-full mr-1">
            <div className="mx-auto px-3 pb-10 backdrop-blur-md border border-white/5 bg-white/5 rounded-lg shadow-sm">
                <div className={`grid gap-4 mt-4 grid-cols-1`}>
                    {/* LEFT: main content */}
                    <div className="min-w-0">
                        {/* Charts row */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2 rounded-lg bg-white shadow-sm border border-gray-200 p-4 h-[320px]">
                                <div className="flex text-sm font-medium items-center justify-between">
                                    <span>History</span>
                                    <select
                                        className="ml-2 text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                                        value={period}
                                        onChange={(e) => {
                                            const newPeriod = parseInt(e.target.value);
                                            setPeriod(newPeriod);
                                        }}>
                                        <option value={7}>7 days</option>
                                        <option value={30}>30 days</option>
                                    </select>
                                </div>
                                {weeklyLoading ?
                                    <div className="flex h-full items-center justify-center pb-16">
                                        <ComponentLoadingBar isLoading={true} />
                                    </div>
                                    : (
                                        <>
                                            <div className="h-full pb-3">
                                                <LineChart
                                                    series={historyDataInPeriod}
                                                    empty={weeklyMetrics?.length === 0}
                                                />
                                            </div>
                                        </>
                                    )}
                            </div>

                            <div className="grid grid-cols-1 grid-rows-5 col-span-1 h-[320px]">
                                <div className="col-span-1 row-span-1 flex mb-2 gap-2">
                                    <KPICard title="Total Sightings" value={kpiSightings} loading={kpiSightings === null} />
                                    <KPICard title="Non-expired IOCs" value={kpiIndicators} loading={kpiIndicators === null} />
                                </div>
                                <div className="col-span-1 row-span-4 rounded-lg bg-white shadow-sm border border-gray-200 p-4">
                                    <div className="text-sm font-medium mb-2">Today</div>
                                    <div>
                                        {dailyLoading ?
                                            <div className="flex h-full items-center justify-center pb-16">
                                                <ComponentLoadingBar isLoading={true} />
                                            </div>
                                            : (
                                                <BarChart
                                                    bars={todayBars}
                                                    loading={dailyMetrics === null}
                                                    empty={(dailyMetrics ?? []).length === 0}
                                                />
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* History table */}
                        <div className="mt-4 rounded-lg bg-white shadow-sm border border-gray-200 p-4">
                            <span className="flex items-center justify-between">
                                <span className="text-sm font-medium mb-2">Sightings</span>
                                <span>
                                    <button
                                        onClick={toggleStream}
                                        className="text-xs rounded-md border border-gray-300 px-2 py-1 bg-white hover:bg-gray-50 shadow-sm"
                                    >
                                        {streamEnabled ? "Pause Live Updates" : "Resume Live Updates"}
                                    </button>
                                </span>
                            </span>
                            {tableLoading ?
                                <div className="flex h-32 items-center justify-center">
                                    <ComponentLoadingBar isLoading={true} />
                                </div>
                                :
                                <DataTable tableItems={tableItems} streamItems={localStreamItems} loading={tableItems.length === 0} animateNewItems={true} />
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SightingsLayout;
